import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useToast } from '../context/ToastContext';
import Loading from '../components/Loading';

const PAY_BY_OPTIONS = ['Cash', 'Bank', 'UPI', 'Cheque', 'Card', 'Other'];

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const emptyForm = () => ({
  date: todayISO(),
  expenseNature: '',
  contractorName: '',
  billNumber: '',
  amount: '',
  paidBy: 'Cash',
  paidAmount: '',
});

const ExpensesSheet = () => {
  const [entries, setEntries] = useState([]);
  const [natures, setNatures] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [filterNature, setFilterNature] = useState('');
  const [filterContractor, setFilterContractor] = useState('');
  const [filterBillNumber, setFilterBillNumber] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const toast = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [entriesRes, naturesRes, contractorsRes] = await Promise.all([
        axios.get('/api/sheet/entries'),
        axios.get('/api/sheet/natures'),
        axios.get('/api/sheet/contractors'),
      ]);
      setEntries(entriesRes.data);
      setNatures(naturesRes.data);
      setContractors(contractorsRes.data);
    } catch (error) {
      toast.error('Failed to load the expenses sheet');
    } finally {
      setLoading(false);
    }
  };

  const filteredEntries = useMemo(() => {
    let list = entries;
    const q = searchTerm.trim().toLowerCase();
    if (q) {
      list = list.filter((e) =>
        (e.expenseNature || '').toLowerCase().includes(q) ||
        (e.contractorName || '').toLowerCase().includes(q) ||
        (e.billNumber || '').toLowerCase().includes(q) ||
        (e.paidBy || '').toLowerCase().includes(q)
      );
    }
    if (filterDate) list = list.filter((e) => (e.date || '').slice(0, 10) === filterDate);
    if (filterNature) list = list.filter((e) => e.expenseNature === filterNature);
    if (filterContractor) list = list.filter((e) => e.contractorName === filterContractor);
    if (filterBillNumber.trim()) {
      const b = filterBillNumber.trim().toLowerCase();
      list = list.filter((e) => (e.billNumber || '').toLowerCase().includes(b));
    }
    return list;
  }, [entries, searchTerm, filterDate, filterNature, filterContractor, filterBillNumber]);

  const totals = useMemo(() => {
    const totalAmount = filteredEntries.reduce((s, e) => s + (Number(e.amount) || 0), 0);
    const totalPaid = filteredEntries.reduce((s, e) => s + (Number(e.paidAmount) || 0), 0);
    const totalBalance = filteredEntries.reduce((s, e) => s + (Number(e.balanceAmount) || 0), 0);
    return { totalAmount, totalPaid, totalBalance };
  }, [filteredEntries]);

  const balance = useMemo(() => {
    return (parseFloat(form.amount) || 0) - (parseFloat(form.paidAmount) || 0);
  }, [form.amount, form.paidAmount]);

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFormError('');
    setModalOpen(true);
  };

  const openEdit = (entry) => {
    setEditingId(entry._id);
    setForm({
      date: (entry.date || '').slice(0, 10),
      expenseNature: entry.expenseNature || '',
      contractorName: entry.contractorName || '',
      billNumber: entry.billNumber || '',
      amount: entry.amount ?? '',
      paidBy: entry.paidBy || 'Cash',
      paidAmount: entry.paidAmount ?? '',
    });
    setFormError('');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setForm(emptyForm());
    setFormError('');
  };

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setFormError('');
  };

  const handleAddNature = async () => {
    const name = window.prompt('Enter new expense nature:');
    if (!name || !name.trim()) return;
    try {
      const res = await axios.post('/api/sheet/natures', { name: name.trim() });
      setNatures((prev) =>
        [...prev.filter((n) => n.name !== res.data.name), res.data].sort((a, b) => a.name.localeCompare(b.name))
      );
      setForm((prev) => ({ ...prev, expenseNature: res.data.name }));
      toast.success('Expense nature added');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add nature');
    }
  };

  const handleAddContractor = async () => {
    const name = window.prompt('Enter new contractor name:');
    if (!name || !name.trim()) return;
    try {
      const res = await axios.post('/api/sheet/contractors', { name: name.trim() });
      setContractors((prev) =>
        [...prev.filter((c) => c.name !== res.data.name), res.data].sort((a, b) => a.name.localeCompare(b.name))
      );
      setForm((prev) => ({ ...prev, contractorName: res.data.name }));
      toast.success('Contractor added');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add contractor');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!form.date || !form.expenseNature || !form.contractorName || form.amount === '' || form.amount === null) {
      return setFormError('Date, Expense Nature, Contractor Name and Amount are required');
    }
    if (parseFloat(form.amount) < 0) return setFormError('Amount cannot be negative');
    if (parseFloat(form.paidAmount) < 0) return setFormError('Paid amount cannot be negative');

    setSaving(true);
    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount),
        paidAmount: parseFloat(form.paidAmount) || 0,
      };

      if (editingId) {
        const res = await axios.patch(`/api/sheet/entries/${editingId}`, payload);
        setEntries((prev) => prev.map((e) => (e._id === editingId ? res.data : e)));
        toast.success('Expense updated');
      } else {
        const res = await axios.post('/api/sheet/entries', payload);
        setEntries((prev) => [res.data, ...prev]);
        toast.success('Expense added');
      }
      closeModal();
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to save expense');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`Delete this expense entry (${entry.expenseNature} - ${entry.contractorName})?`)) return;
    try {
      await axios.delete(`/api/sheet/entries/${entry._id}`);
      setEntries((prev) => prev.filter((e) => e._id !== entry._id));
      toast.success('Expense deleted');
    } catch (err) {
      toast.error('Failed to delete expense');
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(Number(amount) || 0);

  const formatDate = (d) => {
    if (!d) return '';
    const dt = new Date(`${d.slice(0, 10)}T00:00:00`);
    return dt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) return <Loading />;

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Expenses Sheet</h1>
          <p className="page-subtitle">Track contractor bills, payments and outstanding balances</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Expense</button>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon blue">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="5" width="20" height="14" rx="2" />
              <line x1="2" y1="10" x2="22" y2="10" />
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-label">Total Amount</div>
            <div className="stat-value">{formatCurrency(totals.totalAmount)}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon green">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-label">Total Paid</div>
            <div className="stat-value">{formatCurrency(totals.totalPaid)}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon yellow">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-label">Total Balance</div>
            <div className="stat-value">{formatCurrency(totals.totalBalance)}</div>
          </div>
        </div>
      </div>

      <div className="filters" style={{ flexWrap: 'wrap', gap: '12px' }}>
        <input
          type="text"
          className="form-input"
          style={{ flex: '1 1 220px', minWidth: '180px' }}
          placeholder="Search nature, contractor, bill no..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <input
          type="date"
          className="form-input"
          title="Filter by date"
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
        />
        <select className="form-select" value={filterNature} onChange={(e) => setFilterNature(e.target.value)}>
          <option value="">All Natures</option>
          {natures.map((n) => (
            <option key={n._id} value={n.name}>{n.name}</option>
          ))}
        </select>
        <select className="form-select" value={filterContractor} onChange={(e) => setFilterContractor(e.target.value)}>
          <option value="">All Contractors</option>
          {contractors.map((c) => (
            <option key={c._id} value={c.name}>{c.name}</option>
          ))}
        </select>
        <input
          type="text"
          className="form-input"
          placeholder="Bill number"
          value={filterBillNumber}
          onChange={(e) => setFilterBillNumber(e.target.value)}
        />
      </div>

      <div className="card">
        <div className="card-body">
          {filteredEntries.length === 0 ? (
            <div className="empty-state">
              <svg className="empty-state-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="2" y="5" width="20" height="14" rx="2" />
                <line x1="2" y1="10" x2="22" y2="10" />
              </svg>
              <h3 className="empty-state-title">
                {entries.length === 0 ? 'No expenses yet' : 'No matching expenses'}
              </h3>
              <p className="empty-state-text">
                {entries.length === 0
                  ? 'Add your first expense to start tracking bills and balances'
                  : 'Try adjusting your search or filters'}
              </p>
              {entries.length === 0 && (
                <button className="btn btn-primary" onClick={openAdd}>Add Expense</button>
              )}
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Expense Nature</th>
                    <th>Contractor</th>
                    <th>Bill No.</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                    <th>Paid By</th>
                    <th style={{ textAlign: 'right' }}>Paid</th>
                    <th style={{ textAlign: 'right' }}>Balance</th>
                    <th style={{ textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEntries.map((e) => (
                    <tr key={e._id}>
                      <td>{formatDate(e.date)}</td>
                      <td>{e.expenseNature}</td>
                      <td>{e.contractorName}</td>
                      <td>{e.billNumber || '—'}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(e.amount)}</td>
                      <td>
                        <span className="badge" style={{ background: 'var(--gray-100)', color: 'var(--gray-600)' }}>
                          {e.paidBy}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(e.paidAmount)}</td>
                      <td
                        style={{
                          textAlign: 'right',
                          fontWeight: 600,
                          color: Number(e.balanceAmount) > 0 ? 'var(--danger)' : 'var(--success)',
                        }}
                      >
                        {formatCurrency(e.balanceAmount)}
                      </td>
                      <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                        <button className="btn btn-outline btn-sm" onClick={() => openEdit(e)}>Edit</button>{' '}
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(e)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {modalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editingId ? 'Edit Expense' : 'Add Expense'}</h3>
              <button className="modal-close" onClick={closeModal}>✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {formError && <div className="alert alert-error">{formError}</div>}

                <div className="form-group">
                  <label className="form-label">Date *</label>
                  <input type="date" name="date" className="form-input" value={form.date} onChange={handleChange} required />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px', alignItems: 'end' }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Expense Nature *</label>
                    <select name="expenseNature" className="form-select" value={form.expenseNature} onChange={handleChange} required>
                      <option value="">Select nature</option>
                      {natures.map((n) => (
                        <option key={n._id} value={n.name}>{n.name}</option>
                      ))}
                    </select>
                  </div>
                  <button type="button" className="btn btn-outline btn-sm" onClick={handleAddNature} title="Add new nature">+</button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px', alignItems: 'end', marginTop: '16px' }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Contractor Name *</label>
                    <select name="contractorName" className="form-select" value={form.contractorName} onChange={handleChange} required>
                      <option value="">Select contractor</option>
                      {contractors.map((c) => (
                        <option key={c._id} value={c.name}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <button type="button" className="btn btn-outline btn-sm" onClick={handleAddContractor} title="Add new contractor">+</button>
                </div>

                <div className="form-group">
                  <label className="form-label">Bill Number</label>
                  <input
                    type="text"
                    name="billNumber"
                    className="form-input"
                    placeholder="e.g. INV-1234"
                    value={form.billNumber}
                    onChange={handleChange}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div className="form-group">
                    <label className="form-label">Amount (₹) *</label>
                    <input
                      type="number"
                      name="amount"
                      className="form-input"
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      value={form.amount}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Paid By</label>
                    <select name="paidBy" className="form-select" value={form.paidBy} onChange={handleChange}>
                      {PAY_BY_OPTIONS.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div className="form-group">
                    <label className="form-label">Paid Amount (₹)</label>
                    <input
                      type="number"
                      name="paidAmount"
                      className="form-input"
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      value={form.paidAmount}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Balance Amount</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formatCurrency(balance)}
                      disabled
                      style={{ background: 'var(--gray-100)', fontWeight: 600, color: balance > 0 ? 'var(--danger)' : 'var(--success)' }}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editingId ? 'Update' : 'Add Expense'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpensesSheet;
